import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const newClient = () => ({
  companyName: "",
  companyNumber: "",
  vehicleCount: "",
  address: "",
  contactPerson: "",
  contactNumber: "",
  contactEmail: "",
  role: "", // "TM" | "ETM" | "Consultant"
});

const MAX_CLIENTS = 10;
const LEGAL_VEHICLE_LIMIT = 50;
const NEAR_LIMIT_THRESHOLD = 45;
const LEGAL_TM_CLIENTS = 4;

export default function ProfilePage() {
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [accountType, setAccountType] = useState("individual");
  const [companyName, setCompanyName] = useState("");
  const [companyReg, setCompanyReg] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");

  const [clients, setClients] = useState([newClient()]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // ---- Load profile ----
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const d = snap.data() || {};
          setName(d.name || user.displayName || "");
          setEmail(d.email || user.email || "");
          setPhone(d.phone || "");
          setAccountType(d.accountType || "individual");
          setCompanyName(d.companyName || "");
          setCompanyReg(d.companyReg || "");
          setCompanyAddress(d.companyAddress || "");
          const list = Array.isArray(d.clients) ? d.clients : [];
          setClients(list.length ? list : [newClient()]);
        } else {
          setName(user.displayName || "");
          setEmail(user.email || "");
          setClients([newClient()]);
        }
      } catch (e) {
        console.error("Failed to load profile:", e);
      } finally {
        setLoaded(true);
      }
    })();
  }, [user?.uid]);

  // ---- Vehicle maths ----
  const totalVehicles = useMemo(() => {
    return clients.reduce((sum, c) => {
      const n = parseInt((c.vehicleCount || "").toString().replace(/\D/g, ""), 10);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [clients]);

  const nearLimit = totalVehicles >= NEAR_LIMIT_THRESHOLD && totalVehicles < LEGAL_VEHICLE_LIMIT;
  const atLimit = totalVehicles >= LEGAL_VEHICLE_LIMIT;

  // Count TM/ETM roles
  const tmCount = useMemo(
    () => clients.filter(c => c.role === "TM" || c.role === "ETM").length,
    [clients]
  );

  // When changing a single client's vehicleCount or role, cap so total never exceeds 50 and TM/ETM never exceeds 4
  const handleClientChange = (index, field, value) => {
    const next = [...clients];

    if (field === "role" && (value === "TM" || value === "ETM")) {
      // Count TM/ETM roles excluding this client
      const tmCountExcluding = clients.reduce((sum, c, i) => {
        if (i === index) return sum;
        return sum + ((c.role === "TM" || c.role === "ETM") ? 1 : 0);
      }, 0);
      if (tmCountExcluding >= LEGAL_TM_CLIENTS) {
        alert(
          `You can only have up to ${LEGAL_TM_CLIENTS} clients as Transport Manager or External Transport Manager. Please select Consultant for this client.`
        );
        return;
      }
    }

    if (field === "vehicleCount") {
      // normalise to digits only
      const digits = (value || "").toString().replace(/\D/g, "");
      const wanted = digits === "" ? 0 : parseInt(digits, 10);

      // sum of all OTHER clients
      const otherTotal = clients.reduce((sum, c, i) => {
        if (i === index) return sum;
        const n = parseInt((c.vehicleCount || "").toString().replace(/\D/g, ""), 10);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);

      const maxForThis = Math.max(0, LEGAL_VEHICLE_LIMIT - otherTotal);
      const finalVal = Math.min(wanted, maxForThis);
      if (wanted > maxForThis) {
        alert(
          `Legal limit is ${LEGAL_VEHICLE_LIMIT} vehicles total.\nYou can assign at most ${maxForThis} here.`
        );
      }
      next[index][field] = finalVal === 0 ? "" : String(finalVal);
    } else {
      next[index][field] = value;
    }

    setClients(next);
  };

  // Allow adding up to 10 clients, but only 4 TM/ETM roles
  const addClient = () => {
    if (clients.length >= MAX_CLIENTS) return;
    setClients([...clients, newClient()]);
  };

  const removeClient = (i) => {
    const next = clients.filter((_, idx) => idx !== i);
    setClients(next.length ? next : [newClient()]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return alert("Not signed in.");

    if (totalVehicles > LEGAL_VEHICLE_LIMIT) {
      alert(
        `Total vehicles (${totalVehicles}) exceed the legal limit of ${LEGAL_VEHICLE_LIMIT}. Please adjust before saving.`
      );
      return;
    }

    setSaving(true);
    try {
      await setDoc(
        doc(db, "users", user.uid),
        {
          name,
          email,
          phone,
          accountType,
          companyName: accountType === "company" ? companyName : "",
          companyReg: accountType === "company" ? companyReg : "",
          companyAddress: accountType === "company" ? companyAddress : "",
          clients,
          totalVehicles,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      alert("Profile saved");
    } catch (e) {
      console.error("Save failed:", e);
      alert("Could not save profile. Check Firestore rules and network.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return <div className="p-8">Please sign in to manage your profile.</div>;
  if (!loaded) return <div className="p-8">Loading profile…</div>;

  return (
    <div className="p-8">
      {/* PRINT CSS for client list */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #ClientListPrint, #ClientListPrint * { visibility: visible; }
          #ClientListPrint { position: absolute; left:0; top:0; width:100%; margin:0; padding:0; box-shadow:none; border:none; }
          @page { size: A4; margin: 12mm; }
          .no-print { display: none !important; }
        }
      `}</style>

      <h2 className="text-3xl font-semibold text-center mb-4">Profile</h2>

      <div className="mb-6 p-4 bg-yellow-100 border-l-4 border-yellow-500">
        <strong className="block text-xl text-red-700 mb-2">LEGAL NOTICE</strong>
        <span className="block text-lg font-bold text-gray-900">
          A Transport Manager (internal or external) may be responsible for a maximum of <span className="underline">4 clients</span> and <span className="underline">50 vehicles</span> in total, as per DVSA/OTC legislation.
        </span>
        <span className="block text-sm text-gray-700 mt-2">
          The system allows up to 10 clients to accommodate consultancy roles, but statutory responsibility must not exceed the legal limits above.
        </span>
      </div>

      {/* Vehicle Counter + Warnings */}
      <div className="mb-4 p-3 border rounded bg-white flex items-center justify-between">
        <div>
          <div className="font-medium">
            Total Vehicles (all clients): <strong>{totalVehicles}</strong> / {LEGAL_VEHICLE_LIMIT}
          </div>
          {nearLimit && !atLimit && (
            <div className="text-amber-700 text-sm">
              Heads-up: You’re at {totalVehicles}. You’re close to the legal limit ({LEGAL_VEHICLE_LIMIT}).
            </div>
          )}
          {atLimit && (
            <div className="text-red-700 text-sm">
              You’ve reached the legal limit of {LEGAL_VEHICLE_LIMIT} vehicles. You cannot assign more.
            </div>
          )}
        </div>
        {/* Print Client List (shows only in screen) */}
        <button
          type="button"
          onClick={() => window.print()}
          className="no-print px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Print Client List
        </button>
      </div>

      {/* Hidden printable client list */}
      <div id="ClientListPrint" className="bg-white p-4 border rounded mb-6">
        <h3 className="text-xl font-semibold mb-2">Client List</h3>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border p-2 text-left">#</th>
              <th className="border p-2 text-left">Company Name</th>
              <th className="border p-2 text-left">Company No.</th>
              <th className="border p-2 text-left">Address</th>
              <th className="border p-2 text-left">Contact</th>
              <th className="border p-2 text-left">Contact Email</th>
              <th className="border p-2 text-left">Role</th>
              <th className="border p-2 text-right">Vehicles</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c, i) => (
              <tr key={i}>
                <td className="border p-2">{i + 1}</td>
                <td className="border p-2">{c.companyName || '-'}</td>
                <td className="border p-2">{c.companyNumber || '-'}</td>
                <td className="border p-2">{c.address || '-'}</td>
                <td className="border p-2">{c.contactPerson || c.contactNumber || '-'}</td>
                <td className="border p-2">{c.contactEmail || '-'}</td>
                <td className="border p-2">{c.role || '-'}</td>
                <td className="border p-2 text-right">{c.vehicleCount || '0'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 text-sm">
          You have <strong>{clients.length}</strong> client(s) and <strong>{totalVehicles}</strong> vehicle(s) in total.
        </div>
        <div className="mt-1 text-xs text-gray-600">
          Tip: Select “(Blank Client) — no auto-fill” on the Documents page if you don’t want client data on a document.
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account + Contact */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 border rounded">
          <div>
            <label className="block text-lg mb-2">Account Type</label>
            <select
              className="w-full p-3 border rounded"
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
            >
              <option value="individual">Individual</option>
              <option value="company">Company</option>
            </select>
          </div>
          <div>
            <label className="block text-lg mb-2">
              {accountType === "company" ? "Contact Name" : "Name"}
            </label>
            <input
              className="w-full p-3 border rounded"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-lg mb-2">Email</label>
            <input
              type="email"
              className="w-full p-3 border rounded"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-lg mb-2">Phone Number</label>
            <input
              className="w-full p-3 border rounded"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        {accountType === "company" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 border rounded">
            <div>
              <label className="block text-lg mb-2">Company Name</label>
              <input
                className="w-full p-3 border rounded"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-lg mb-2">Company Reg Number</label>
              <input
                className="w-full p-3 border rounded"
                value={companyReg}
                onChange={(e) => setCompanyReg(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-lg mb-2">Company Address</label>
              <input
                className="w-full p-3 border rounded"
                value={companyAddress}
                onChange={(e) => setCompanyAddress(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Client management */}
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-semibold">Clients</h3>
          <div className="text-sm text-gray-600">
            {clients.length} / {MAX_CLIENTS}
          </div>
        </div>

        <div className="mb-2">
          <button
            type="button"
            onClick={addClient}
            disabled={clients.length >= MAX_CLIENTS}
            className={`px-3 py-2 rounded ${
              clients.length >= MAX_CLIENTS
                ? "bg-gray-300"
                : "bg-blue-600 hover:bg-blue-700"
            } text-white`}
          >
            Add Client
          </button>
          {tmCount >= LEGAL_TM_CLIENTS && (
            <div className="text-red-600 text-sm mt-2">
              You have reached the maximum of {LEGAL_TM_CLIENTS} Transport Manager/External Transport Manager clients. Any additional clients must be added as Consultant.
            </div>
          )}
        </div>

        {clients.map((client, index) => (
          <div key={index} className="mb-6 border p-4 rounded-lg bg-white">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-xl font-semibold">Client #{index + 1}</h4>
              <button
                type="button"
                onClick={() => removeClient(index)}
                className="text-red-600 hover:underline"
              >
                Remove
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-lg mb-2">Role</label>
                <select
                  className="w-full p-3 border rounded"
                  value={client.role}
                  onChange={e =>
                    handleClientChange(index, "role", e.target.value)
                  }
                  required
                >
                  <option value="">Select Role</option>
                  <option value="TM">Transport Manager</option>
                  <option value="ETM">External Transport Manager</option>
                  <option value="Consultant">Consultant</option>
                </select>
              </div>
              <div>
                <label className="block text-lg mb-2">Company Name</label>
                <input
                  className="w-full p-3 border rounded"
                  value={client.companyName}
                  onChange={e =>
                    handleClientChange(index, "companyName", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Company Number</label>
                <input
                  className="w-full p-3 border rounded"
                  value={client.companyNumber}
                  onChange={e =>
                    handleClientChange(index, "companyNumber", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Vehicle Count</label>
                <input
                  className="w-full p-3 border rounded"
                  value={client.vehicleCount}
                  onChange={e =>
                    handleClientChange(index, "vehicleCount", e.target.value)
                  }
                  placeholder="e.g. 5"
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Address</label>
                <input
                  className="w-full p-3 border rounded"
                  value={client.address}
                  onChange={e =>
                    handleClientChange(index, "address", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Point of Contact</label>
                <input
                  className="w-full p-3 border rounded"
                  value={client.contactPerson}
                  onChange={e =>
                    handleClientChange(index, "contactPerson", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Contact Number</label>
                <input
                  className="w-full p-3 border rounded"
                  value={client.contactNumber}
                  onChange={e =>
                    handleClientChange(index, "contactNumber", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="block text-lg mb-2">Contact Email</label>
                <input
                  type="email"
                  className="w-full p-3 border rounded"
                  value={client.contactEmail}
                  onChange={e =>
                    handleClientChange(index, "contactEmail", e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        ))}

        <div className="mt-8">
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-blue-600 text-white p-4 rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Profile"}
          </button>
        </div>
      </form>
    </div>
  );
}
